import { DXAReading } from '@dynamika-shared/types/DXAReading';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NestedFilter } from 'src/components/NestedFilter/NestedFilter';
import { toggleFilterState } from '@restore/usersFilters/toggleFilterState';

type PossibleFilters = 'bodyRegion' | 'bodyProperty';

type FilterState = {
  selectedFilters: Record<PossibleFilters, string[]>;
  allFilters: Record<PossibleFilters, { label: string; value: string }[]>;
  state: Record<PossibleFilters, { active: boolean; label: string }>;
};

const filterStateInitial: FilterState = {
  selectedFilters: {
    bodyRegion: [],
    bodyProperty: [],
  },
  allFilters: {
    bodyRegion: [],
    bodyProperty: [],
  },
  state: {
    bodyRegion: {
      active: false,
      label: 'Region',
    },
    bodyProperty: {
      active: false,
      label: 'Laterality/Vertebra',
    },
  },
};

export const useFilters = (
  workstationReadings: DXAReading[],
  className = ''
) => {
  const [filterState, setFilterState] = useState(filterStateInitial);

  const filters = useMemo(() => {
    return [
      {
        key: '2',
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: Object.entries(filterState.state).map(([key, filter]) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox
                checked={!!filterState.state?.[key as PossibleFilters]?.active}
              />
            </div>
          ),
        })),
      },
    ];
  }, [filterState.state]);

  const filteredReadings = useMemo(() => {
    const { bodyProperty, bodyRegion } = filterState.selectedFilters;
    if (!bodyRegion.length && !bodyProperty.length) {
      return workstationReadings;
    }

    if (bodyRegion.length && bodyProperty.length) {
      return workstationReadings.filter(reading => {
        return (
          bodyRegion.includes(reading.bodyRegion ?? '') &&
          bodyProperty.includes(reading.bodyProperty ?? '')
        );
      });
    }

    if (bodyRegion.length) {
      return workstationReadings.filter(reading => {
        return bodyRegion.includes(reading.bodyRegion ?? '');
      });
    }

    if (bodyProperty.length) {
      return workstationReadings.filter(reading => {
        return bodyProperty.includes(reading.bodyProperty ?? '');
      });
    }
  }, [filterState.selectedFilters, workstationReadings]);

  const toggleSelectedFilter = useCallback(
    (name: PossibleFilters, value: string) => {
      const selectedFilters = filterState.selectedFilters[name];
      const newSelectedFilters = selectedFilters.includes(value)
        ? selectedFilters.filter(val => val !== value)
        : [...selectedFilters, value];

      setFilterState({
        ...filterState,
        selectedFilters: {
          ...filterState.selectedFilters,
          [name]: newSelectedFilters,
        },
      });
    },
    [filterState.selectedFilters]
  );

  const closeTag = useCallback(
    (name: PossibleFilters) => {
      setFilterState({
        ...filterState,
        selectedFilters: {
          ...filterState.selectedFilters,
          [name]: [],
        },
        state: {
          ...filterState.state,
          [name]: {
            ...filterState.state[name],
            active: false,
          },
        },
      });
    },
    [filterState.state, filterState.selectedFilters]
  );

  const filterOnClick = useCallback(
    (item: MenuInfo) => {
      const newState = toggleFilterState(
        filterState,
        item?.key as PossibleFilters
      );

      setFilterState(newState);
    },
    [filterState]
  );

  useEffect(() => {
    const [uniqProperty, uniqRegion]: string[][] = [[], []];

    workstationReadings.forEach(({ bodyProperty, bodyRegion }) => {
      if (bodyProperty && !uniqProperty.includes(bodyProperty)) {
        uniqProperty.push(bodyProperty);
      }
      if (bodyRegion && !uniqRegion.includes(bodyRegion)) {
        uniqRegion.push(bodyRegion);
      }
    });

    const s = {
      ...filterState,
      allFilters: {
        bodyProperty: uniqProperty.map(value => ({ label: value, value })),
        bodyRegion: uniqRegion.map(value => ({ label: value, value })),
      },
    };

    setFilterState(s);
  }, [workstationReadings]);

  const filterSection = (
    <div className={className}>
      <NestedFilter<'bodyRegion' | 'bodyProperty', string>
        allFilters={filterState.allFilters}
        closeTag={closeTag}
        selectedFilters={filterState.selectedFilters}
        items={filters}
        onClick={filterOnClick}
        subFilters={Object.entries(filterState.state) as []}
        toggleSelectedFilter={toggleSelectedFilter}
      />
    </div>
  );

  return { filteredReadings, filterSection };
};
